// ---------- la feuille de partage : l'image ou la vidéo de la feuille terminée ----------
// Le bouton « Partager » de fin de partie l'ouvre (cf. wireUI dans ui.ts), là où le navigateur sait
// partager un fichier. Trois temps, décidés le 2026-10-16 :
//   1. le CHOIX — l'image du dessin fini, ou la vidéo de son remplissage ;
//   2. la PRÉPARATION, avec sa progression : la vidéo se filme en temps réel (cf. src/video.ts),
//      l'image en un instant ;
//   3. l'ENVOI, sur un bouton à lui.
//
// Le troisième temps n'est pas une politesse : le navigateur n'ouvre la fenêtre de partage qu'à la
// suite IMMÉDIATE d'un geste du joueur, et huit secondes d'enregistrement suffisent à le périmer —
// `navigator.share` refusait alors sans rien montrer (premier essai, 2026-10-16). « Envoyer » est ce
// geste-là.
//
// Une création part sans lien (personne d'autre ne peut l'ouvrir, cf. backend/src/routes/
// creations.ts) ; une feuille du catalogue part avec le lien de sa page dans le texte, et compte
// au badge « Partage ». L'appelant décide des deux (cf. ShareContext).

import { renderThumb } from './render';
import { S } from './state';
import { t } from './i18n';
import { buzz, shareFile } from './platform';
import { el, icon } from './html';
import { noteCreationShare, noteShare } from './shares';
import { recordFillVideo, videoMime } from './video';
import { brandBand, brandPainter, loadImage, withTimeout } from './brand';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

type Kind = 'image' | 'video';

export interface ShareContext {
  /** Une création du joueur (sa photo), ou une feuille du catalogue : le message n'est pas le même. */
  mine: boolean;
  /** L'identifiant à compter au badge « Partage », ou null (une création). */
  countAs: string | null;
  /** La création partagée, pour le compteur de l'atelier (cf. noteCreationShare), ou null (une
   *  feuille du catalogue, déjà comptée par `countAs`). */
  creationId?: string | null;
}

/**
 * LE LIEN QUI VOYAGE AVEC LE FICHIER : la fiche Play Store, toujours — demandé le 2026-10-16, et
 * c'est le même parti que le bouton « Partager le jeu » des réglages (cf. src/maker.ts) : c'est
 * l'installation qui fait vivre le jeu. Pas le lien de la page de la feuille : une création n'en a
 * pas (elle est privée), et pour une feuille du catalogue la vidéo montre déjà tout ce que la page
 * montrerait.
 *
 * Une provenance par sorte de fichier (`partage-video`, `partage-image`), à côté de `partage` (la
 * page d'une feuille) et `reglages` : la Console Play distingue ainsi ce que chaque bouton amène.
 * Le referrer est doublement encodé, et il FAUT qu'il le reste — Play le redécode lui-même.
 */
function playUrl(kind: Kind): string {
  return (
    'https://play.google.com/store/apps/details?id=fr.tidou44.codigrid' +
    `&referrer=utm_source%3Dpartage-${kind === 'video' ? 'video' : 'image'}%26utm_medium%3Dapp`
  );
}

/**
 * Le message qui accompagne le fichier : quatre, selon ce qu'on envoie et d'où vient la feuille.
 * Écrits pour être lus par quelqu'un qui ne connaît pas le jeu — ce qu'il voit, ce que c'est, où
 * le trouver — et le lien en dernier, seul sur sa ligne, pour que les messageries en fassent une
 * carte d'aperçu.
 */
function messageFor(kind: Kind, mine: boolean): string {
  const key = mine
    ? kind === 'video'
      ? 'shareSheet.msg.mineVideo'
      : 'shareSheet.msg.mineImage'
    : kind === 'video'
      ? 'shareSheet.msg.video'
      : 'shareSheet.msg.image';
  return t(key, { url: playUrl(kind) });
}

/**
 * Ce qui est déjà prêt, pour la feuille ouverte : rouvrir son partage ne refait ni l'image ni,
 * surtout, onze secondes de vidéo.
 *
 * LA FEUILLE OUVERTE SEULEMENT (2026-10-17). Le cache gardait toutes les vidéos de la session,
 * plusieurs Mo chacune, avec leur URL `blob:` jamais libérée : dans la WebView d'un téléphone, une
 * mémoire qui ne fait que monter. Ranger une feuille libère celles des autres (cf. remember).
 */
const made = new Map<string, { blob: Blob; name: string; url: string }>();
const sheetOf = (): string => `${S.puzzleId}|${S.taps.length}`;
const keyOf = (kind: Kind): string => `${sheetOf()}|${kind}`;

function remember(key: string, entry: { blob: Blob; name: string; url: string }): void {
  const sheet = key.slice(0, key.lastIndexOf('|'));
  for (const [k, e] of made)
    if (k.slice(0, k.lastIndexOf('|')) !== sheet) {
      URL.revokeObjectURL(e.url);
      made.delete(k);
    }
  made.set(key, entry);
}

let ctx: ShareContext | null = null;
/** Le jeton de la préparation en cours : refermer la feuille, ou en rouvrir une autre, rend
 *  caduque une vidéo qui finirait de se filmer derrière. */
let token = 0;

export function openShareSheet(c: ShareContext): void {
  ctx = c;
  token++;
  paintChoice();
  $('shareSheet').setAttribute('aria-hidden', 'false');
}

/** Même contrat que closeLangSheet() — ce que la chaîne du bouton retour attend. */
export function closeShareSheet(): boolean {
  if ($('shareSheet').getAttribute('aria-hidden') !== 'false') return false;
  $('shareSheet').setAttribute('aria-hidden', 'true');
  token++;
  return true;
}

export function wireShareSheet(): void {
  $('shareSheetClose').onclick = () => closeShareSheet();
  $('shareSheet').onclick = (e) => {
    if (e.target === $('shareSheet')) closeShareSheet();
  };
}

function body(...children: HTMLElement[]): void {
  $('shareSheetBody').replaceChildren(...children);
}

// ---------- 1. le choix ----------

function paintChoice(): void {
  const row = el('div', 'share-choices');
  const options: [Kind, string][] = [['image', 'i-creation']];
  // La vidéo seulement là où le navigateur sait filmer un canvas.
  if (videoMime()) options.push(['video', 'i-play']);
  for (const [kind, ic] of options) {
    const b = el('button', 'share-choice', `<span class="share-choice-i">${icon(ic)}</span>`);
    const title = el('b');
    title.textContent = t(`shareSheet.${kind}`);
    const sub = el('span');
    sub.textContent = t(`shareSheet.${kind}Sub`);
    b.append(title, sub);
    b.onclick = () => {
      buzz('light');
      void prepare(kind);
    };
    row.append(b);
  }
  body(row);
}

// ---------- 2. la préparation ----------

/** L'anneau de progression : un cercle d'accent qui se remplit, le pourcentage au milieu. */
function progressRing(): { root: HTMLElement; set: (q: number) => void } {
  const R = 44;
  const C = 2 * Math.PI * R;
  const root = el(
    'div',
    'share-ring',
    `<svg viewBox="0 0 100 100" aria-hidden="true">
       <circle class="share-ring-track" cx="50" cy="50" r="${R}"></circle>
       <circle class="share-ring-fill" cx="50" cy="50" r="${R}" stroke-dasharray="${C}" stroke-dashoffset="${C}"></circle>
     </svg>
     <span class="share-ring-n">0 %</span>`,
  );
  root.setAttribute('role', 'progressbar');
  root.setAttribute('aria-valuemin', '0');
  root.setAttribute('aria-valuemax', '100');
  const fill = root.querySelector<SVGCircleElement>('.share-ring-fill')!;
  const n = root.querySelector('.share-ring-n')!;
  return {
    root,
    set: (q) => {
      const pct = Math.round(Math.min(1, Math.max(0, q)) * 100);
      fill.setAttribute('stroke-dashoffset', String(C * (1 - pct / 100)));
      n.textContent = `${pct} %`;
      root.setAttribute('aria-valuenow', String(pct));
    },
  };
}

async function prepare(kind: Kind): Promise<void> {
  const mine = ++token;
  // La clé lue AVANT toute attente : calculée après, elle lisait la feuille ouverte À LA FIN de
  // l'enregistrement — refermer le panneau et ouvrir une autre feuille pendant les onze secondes
  // rangeait la vidéo de la première sous le nom de la seconde (revue du 2026-10-17).
  const key = keyOf(kind);
  const cached = made.get(key);
  if (cached) {
    paintReady(kind, cached);
    return;
  }
  const ring = progressRing();
  const msg = el('p', 'share-msg');
  msg.textContent = t(`shareSheet.${kind}Busy`);
  const wrap = el('div', 'share-busy');
  wrap.append(ring.root, msg);
  body(wrap);
  try {
    let blob: Blob;
    let name: string;
    const day = new Date().toISOString().slice(0, 10);
    if (kind === 'image') {
      ring.set(0.3);
      // Un temps pour que l'anneau se montre : sans lui, l'image (quelques dizaines de ms) ferait
      // clignoter l'écran de préparation sans qu'on puisse le lire.
      await new Promise((r) => setTimeout(r, 250));
      blob = await brandedImage();
      name = `codigrid-${day}.png`;
      ring.set(1);
    } else {
      const v = await recordFillVideo(
        {
          gridW: S.gridW,
          gridH: S.gridH,
          target: S.target,
          alphabetId: S.alphabetId,
          taps: S.taps,
        },
        (q) => {
          if (mine === token) ring.set(q);
        },
      );
      blob = v.blob;
      name = `codigrid-${day}.${v.mime.includes('mp4') ? 'mp4' : 'webm'}`;
    }
    // Refermée, ou rouverte sur autre chose, pendant la préparation : gardée seulement si c'est
    // toujours la feuille ouverte (rouvrir son partage la retrouvera). Celle d'une feuille quittée
    // est jetée — la ranger aurait chassé du cache, et révoqué, ce que montre peut-être déjà le
    // panneau de la nouvelle.
    if (mine !== token && key !== keyOf(kind)) return;
    const entry = { blob, name, url: URL.createObjectURL(blob) };
    remember(key, entry);
    if (mine !== token) return;
    buzz('medium');
    paintReady(kind, entry);
  } catch {
    if (mine !== token) return;
    const err = el('p', 'share-msg');
    err.textContent = t('shareSheet.failed');
    body(err);
  }
}

/**
 * L'image partagée : la feuille terminée, dessinée en grand, sur une marge de papier, et sous elle
 * la bande de signature — le logo et le nom (cf. src/brand.ts), comme la vidéo.
 */
async function brandedImage(): Promise<Blob> {
  const url = renderThumb(1600);
  if (!url) throw new Error('nothing to draw');
  // Avec une limite, comme tout ce que la préparation attend (cf. withTimeout dans brand.ts) : la
  // feuille est la seule pièce sans repli, et sans limite son décodage pouvait figer le partage.
  const sheet = await loadImage(url, 8000, 'l’image de la feuille');
  const paintBrand = await brandPainter();
  const margin = Math.round(sheet.width * 0.06);
  const band = brandBand(sheet.width + 2 * margin);
  const cv = document.createElement('canvas');
  cv.width = sheet.width + 2 * margin;
  cv.height = sheet.height + 2 * margin + band;
  const g = cv.getContext('2d')!;
  // Le papier de la feuille, déjà dans l'image (renderThumbOf le peint), prolongé sur la marge.
  g.fillStyle = '#fbf6ec';
  g.fillRect(0, 0, cv.width, cv.height);
  g.drawImage(sheet, margin, margin);
  paintBrand(
    g,
    margin,
    margin + sheet.width,
    margin + sheet.height + (cv.height - margin - sheet.height) / 2,
    band,
  );
  const blob = new Promise<Blob>((resolve, reject) =>
    cv.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob'))), 'image/png'),
  );
  // `toBlob` encode hors du fil principal et rappelle quand il a fini — s'il ne rappelle jamais, le
  // même PNG s'obtient d'un coup par toDataURL, synchrone : plus lent, mais il répond.
  return withTimeout(blob, 8000, 'l’encodage du PNG').catch(() =>
    dataUrlBlob(cv.toDataURL('image/png')),
  );
}

/** Un `data:` en Blob, sans passer par `fetch` (qui, sur une URL `data:`, n'est pas garanti dans
 *  toutes les WebView). */
function dataUrlBlob(url: string): Blob {
  const [head, b64] = url.split(',');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: head.slice(5, head.indexOf(';')) });
}

// ---------- 3. l'envoi ----------

function paintReady(kind: Kind, entry: { blob: Blob; name: string; url: string }): void {
  const wrap = el('div', 'share-ready');
  // Ce qui va partir, en petit : l'image elle-même, ou la vidéo qui tourne en boucle, sans son.
  const media =
    kind === 'image'
      ? Object.assign(el('img', 'share-media'), { src: entry.url, alt: '' })
      : Object.assign(el('video', 'share-media'), {
          src: entry.url,
          muted: true,
          loop: true,
          autoplay: true,
          playsInline: true,
        });
  const title = el('h3', 'share-ready-t');
  title.textContent = t(`shareSheet.${kind}Ready`);
  const send = el('button', 'btn primary share-send', `${icon('i-share')}<span></span>`);
  const label = send.querySelector('span')!;
  label.textContent = t(`shareSheet.${kind}Send`);
  // LE geste qui ouvre la fenêtre de partage (cf. l'en-tête du fichier) : rien d'asynchrone
  // avant shareFile, sans quoi le navigateur le tiendrait déjà pour périmé.
  send.onclick = () => {
    const c = ctx;
    if (!c) return;
    buzz('light');
    void shareFile(entry.blob, entry.name, messageFor(kind, c.mine)).then((r) => {
      // Seul un vrai partage compte pour le badge « Partage ». Un téléchargement (un navigateur
      // d'ordinateur sans partage de fichiers) n'envoie rien à personne, et le compter laissait
      // monter le badge en enregistrant des images sur son disque (revue du 2026-10-17).
      if (r === 'partagé' && c.countAs) noteShare(c.countAs);
      if (r === 'partagé' && c.creationId) noteCreationShare(c.creationId);
      if (r === 'partagé') closeShareSheet();
      else if (r === 'téléchargé') label.textContent = t(`shareSheet.${kind}Saved`);
    });
  };
  const other = el('button', 'btn share-other');
  other.textContent = t('shareSheet.other');
  other.onclick = () => {
    token++;
    paintChoice();
  };
  wrap.append(media, title, send, other);
  body(wrap);
}
